import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EventsService } from '~/services/domain/EventsService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Ticket02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Ticket02Icon as TicketSolidIcon } from '@hugeicons-pro/core-solid-rounded';
import { useTranslation } from 'react-i18next';
import { MemberDetails } from '~/models/MemberDetails';
import { EventDetails } from '~/models/EventDetails';
import { plainToClass } from '~/models/transform';

interface EventAttendButtonProps {
  eventId: number;
  eventName: string;
  isAttending: boolean;
  userName: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onAttendingChange?: (isAttending: boolean) => void;
}

export function EventAttendButton({
  eventId,
  eventName,
  isAttending: initialIsAttending,
  userName,
  className,
  size = 'sm',
  onAttendingChange,
}: EventAttendButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isAttending, setIsAttending] = useState(initialIsAttending);

  useEffect(() => {
    setIsAttending(initialIsAttending);
  }, [initialIsAttending]);

  const attendMutation = useMutation({
    mutationFn: () => EventsService.attendEvent(eventId),
    onMutate: async () => {
      setIsAttending(true);
      onAttendingChange?.(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-profile', userName] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      showSuccessToast(t('events.attendSuccess'));
    },
    onError: (error) => {
      setIsAttending(false);
      onAttendingChange?.(false);
      showErrorToast(t('events.attendError'), error);
    },
  });

  const unattendMutation = useMutation({
    mutationFn: () => EventsService.unattendEvent(eventId),
    onMutate: async () => {
      setIsAttending(false);
      onAttendingChange?.(false);
      queryClient.setQueryData(['event-profile', userName], (oldData: any) => {
        if (!oldData?.EventDetails) return oldData;
        return plainToClass(MemberDetails, {
          ...oldData,
          EventDetails: plainToClass(EventDetails, {
            ...oldData.EventDetails,
            IsAttending: false,
            IsFollowing: true,
          }),
        });
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-profile', userName] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      showSuccessToast(t('events.unattendSuccess'));
    },
    onError: (error) => {
      setIsAttending(true);
      onAttendingChange?.(true);
      showErrorToast(t('events.unattendError'), error);
    },
  });

  const handlePress = () => {
    if (isAttending) {
      unattendMutation.mutate();
    } else {
      attendMutation.mutate();
    }
  };

  const isPending = attendMutation.isPending || unattendMutation.isPending;

  return (
    <ButtonWrapper
      size={size}
      variant="solid"
      action={isAttending ? 'neutral' : 'primary'}
      icon={isAttending ? TicketSolidIcon : Ticket02Icon}
      iconClassName={isAttending ? 'color-primary-500' : ''}
      iconSize={18}
      text={isAttending ? t('events.attending') : t('events.attend')}
      onPress={handlePress}
      isDisabled={isPending}
      className={className}
    />
  );
}
